// Вертикальная ручка ресайза боковой панели.
// Работает и мышью/тачем (pointer events с захватом), и с клавиатуры —
// это role="separator", у которого стрелки двигают границу.
import { useRef, type KeyboardEvent, type PointerEvent, type ReactElement } from 'react';
import { useT } from '../../i18n';
import { PANEL_MAX_W, PANEL_MIN_W, clampPanelWidth } from './usePanelLayout';
import styles from './PanelResizer.module.css';

/** Шаг клавиатурного ресайза, px. */
const KEY_STEP = 16;

export interface PanelResizerProps {
  /** С какой стороны рабочей области стоит панель: от этого зависит знак смещения. */
  side: 'left' | 'right';
  label: string;
  /** Текущая ширина панели в px — от неё отсчитывается перетаскивание. */
  width: number;
  onResize(px: number): void;
  onResizeStart?(): void;
  onResizeEnd?(): void;
}

export function PanelResizer({
  side,
  label,
  width,
  onResize,
  onResizeStart,
  onResizeEnd,
}: PanelResizerProps): ReactElement {
  const t = useT();
  const dragRef = useRef<{ startX: number; startWidth: number } | null>(null);

  function handlePointerDown(e: PointerEvent<HTMLDivElement>): void {
    // Только основная кнопка: правый клик и средний не должны начинать ресайз.
    if (e.button !== 0) return;
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startWidth: width };
    e.currentTarget.setPointerCapture(e.pointerId);
    onResizeStart?.();
  }

  function handlePointerMove(e: PointerEvent<HTMLDivElement>): void {
    const drag = dragRef.current;
    if (!drag) return;
    const delta = e.clientX - drag.startX;
    // Левая панель растёт вправо, правая — влево.
    onResize(drag.startWidth + (side === 'left' ? delta : -delta));
  }

  function endDrag(e: PointerEvent<HTMLDivElement>): void {
    if (!dragRef.current) return;
    dragRef.current = null;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    onResizeEnd?.();
  }

  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>): void {
    const grow = side === 'left' ? 'ArrowRight' : 'ArrowLeft';
    const shrink = side === 'left' ? 'ArrowLeft' : 'ArrowRight';
    if (e.key === grow) {
      e.preventDefault();
      onResize(width + KEY_STEP);
    } else if (e.key === shrink) {
      e.preventDefault();
      onResize(width - KEY_STEP);
    } else if (e.key === 'Home') {
      e.preventDefault();
      onResize(PANEL_MIN_W);
    } else if (e.key === 'End') {
      e.preventDefault();
      onResize(PANEL_MAX_W);
    }
  }

  return (
    <div
      className={`${styles.resizer} ${side === 'left' ? styles.onRight : styles.onLeft}`}
      role="separator"
      aria-orientation="vertical"
      aria-label={t('app.panel.width', { name: label })}
      aria-valuenow={clampPanelWidth(width)}
      aria-valuemin={PANEL_MIN_W}
      aria-valuemax={PANEL_MAX_W}
      tabIndex={0}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onKeyDown={handleKeyDown}
    >
      <span className={styles.grip} aria-hidden="true" />
    </div>
  );
}
